import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { AlertCircle, Lock, ShieldAlert } from 'lucide-react';
import './AppAccessGuard.css';
import { apiGet, apiPost } from '../lib/api';

// In-memory cache: survives client-side navigation (doesn't persist full reloads).
// This ensures the "Checking your access…" screen only shows once per session,
// not on every re-mount of the guard.
let _entitlementCache = null;

function AppAccessGuard() {
  const navigate = useNavigate();
  const location = useLocation();
  // Seed state from cache if we already checked this session.
  const [accessState, setAccessState] = useState(
    _entitlementCache ? (_entitlementCache.has_access ? 'allowed' : (_entitlementCache.denial_reason === 'account_suspended' ? 'suspended' : 'expired')) : 'loading'
  );
  const [entitlement, setEntitlement] = useState(_entitlementCache);
  const [error, setError] = useState(null);

  useEffect(() => {
    // If we already have a cached result, skip the network check on mount.
    if (_entitlementCache) return;
    checkAccess();
  }, []);

  const checkAccess = async () => {
    setAccessState('loading');
    setError(null);

    try {
      const response = await apiGet('/api/user/entitlement');

      if (response.ok) {
        const data = await response.json();
        _entitlementCache = data; // cache in-memory for this session
        setEntitlement(data);

        if (data.has_access) {
          setAccessState('allowed');
        } else {
          // Check denial reason
          if (data.denial_reason === 'account_suspended') {
            setAccessState('suspended');
          } else {
            // Any other reason (expired, no subscription, etc.)
            setAccessState('expired');
          }
        }
      } else if (response.status === 401) {
        // Not authenticated - redirect to login
        navigate('/', { replace: true });
      } else {
        // Other error - show error state
        setError('Failed to check access. Please refresh.');
        setAccessState('error');
      }
    } catch (err) {
      console.error('Error checking access:', err);
      // Network error — use cached entitlement if available; allow optimistically when offline
      if (_entitlementCache?.has_access) {
        setAccessState('allowed');
        return;
      }
      if (!navigator.onLine) {
        setAccessState('allowed'); // optimistic — server check happens when back online
        return;
      }
      setError('Network error. Please check your connection.');
      setAccessState('error');
    }
  };

  // Loading screen — lighter, branded splash (only shows ONCE per session thanks to in-memory cache)
  if (accessState === 'loading') {
    return (
      <div className="access-guard-screen" data-testid="access-loading">
        <div className="access-guard-splash">
          <div className="access-splash-brand">
            <span className="access-splash-dot" />
            <span className="access-splash-word">Hourtrix</span>
          </div>
          <div className="access-splash-bar"><span /></div>
        </div>
      </div>
    );
  }

  // Error screen
  if (accessState === 'error') {
    return (
      <div className="access-guard-screen" data-testid="access-error">
        <div className="access-guard-content">
          <AlertCircle size={64} className="icon-error" />
          <h2>Unable to Verify Access</h2>
          <p className="error-message">{error}</p>
          <button onClick={checkAccess} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Suspended screen
  if (accessState === 'suspended') {
    return (
      <div className="access-guard-screen suspended" data-testid="access-suspended">
        <div className="access-guard-content">
          <div className="icon-container suspended">
            <ShieldAlert size={64} />
          </div>
          <h1>Account Suspended</h1>
          <p className="suspension-message">
            Your account has been temporarily suspended.
          </p>
          {entitlement?.suspension_reason && (
            <div className="reason-box">
              <p className="reason-label">Reason:</p>
              <p className="reason-text">{entitlement.suspension_reason}</p>
            </div>
          )}
          <div className="suspended-actions">
            <button 
              onClick={() => window.location.href = 'mailto:idreesrahman04@gmail.com'}
              className="contact-support-button"
              data-testid="contact-support-button"
            >
              Contact Support
            </button>
            <button 
              onClick={() => navigate('/')}
              className="go-home-button"
            >
              Go to Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Expired/No Access — show paywall
  if (accessState === 'expired') {
    return <PaywallScreen entitlement={entitlement} />;
  }

  // Access allowed - render protected routes
  return <Outlet />;
}

// Paywall screen component
function PaywallScreen({ entitlement }) {
  const navigate = useNavigate();
  const [showPaywall, setShowPaywall] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);

  // Check if user prefers immediate redirect or paywall
  // For now, show paywall first
  
  if (!showPaywall) {
    // Direct redirect to billing
    navigate('/billing', { replace: true });
    return null;
  }

  const handleLogout = async () => {
    setLoggingOut(true);
    
    try {
      await apiPost('/api/auth/logout', {});
      
      // Redirect to homepage
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, redirect to homepage
      window.location.href = '/';
    }
  };

  const isExpired = entitlement?.denial_reason === 'subscription_expired' || 
                   entitlement?.denial_reason === 'access_period_ended';
  const isTrial = entitlement?.status === 'trial_active';

  return (
    <div className="paywall-screen" data-testid="paywall-screen">
      <div className="paywall-container">
        <div className="paywall-header">
          <div className="paywall-icon">
            <Lock size={48} />
          </div>
          <h1 className="paywall-title">
            {isExpired ? 'Your Trial Has Ended' : 'Subscription Required'}
          </h1>
          <p className="paywall-subtitle">
            {isExpired 
              ? 'Continue tracking your time and building better habits with Hourtrix Pro'
              : 'Unlock full access to all features'
            }
          </p>
        </div>

        <div className="paywall-benefits">
          <h2>What You'll Get</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">⏱️</div>
              <h3>Unlimited Time Tracking</h3>
              <p>Track activities across all categories without limits</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">📊</div>
              <h3>Advanced Analytics</h3>
              <p>Deep insights into your time usage and productivity</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🎯</div>
              <h3>Habit Tracking</h3>
              <p>Build and maintain healthy habits with smart tracking</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🤖</div>
              <h3>AI-Powered Insights</h3>
              <p>Get personalized recommendations and insights</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">📱</div>
              <h3>Multi-Platform Sync</h3>
              <p>Access your data anywhere, on any device</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">💎</div>
              <h3>Priority Support</h3>
              <p>Get help when you need it from our support team</p>
            </div>
          </div>
        </div>

        <div className="paywall-actions">
          <button 
            onClick={() => navigate('/billing')}
            className="upgrade-button"
            data-testid="upgrade-button"
          >
            View Plans & Upgrade
          </button>
          <button 
            onClick={() => navigate('/')}
            className="back-button"
          >
            Back to Homepage
          </button>
          <button 
            onClick={handleLogout}
            className="logout-button"
            disabled={loggingOut}
            data-testid="logout-button"
          >
            {loggingOut ? 'Logging out...' : 'Log Out'}
          </button>
        </div>

        {entitlement && (
          <div className="paywall-status">
            <p className="status-text">
              Status: <span className="status-value">{entitlement.status_display}</span>
            </p>
            {entitlement.days_remaining !== undefined && (
              <p className="days-remaining">
                {entitlement.days_remaining === 0 
                  ? 'Access ended' 
                  : `${entitlement.days_remaining} days remaining`
                }
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function clearEntitlementCache() {
  _entitlementCache = null;
}

export default AppAccessGuard;
